import React, { useMemo, useEffect } from "react";
import useSWR from "swr";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SkeletonTable from "../../Common/SkeletonTable";
import ErrorCard from "../../Common/ErrorCard";
import EmptyCard from "../../Common/EmptyCard";
import PaginationBar from "../../Common/PaginationBar";
import { inventoryFetcher } from "../../../network/InventoryService";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const WarehouseList = ({
  showWarehouseCreateModal,
  setShowWarehouseCreateModal,
  searchTerm,
  onWarehouseView,
  currentPage,
  setCurrentPage,
}) => {
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, setCurrentPage]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: currentPage - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-warehouse?${params.toString()}`;
  }, [currentPage, searchTerm]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const warehouses = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const handleViewWarehouse = (warehouse) => {
    onWarehouseView(warehouse);
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={7}
          rowCount={7}
          headers={[
            "#",
            "Warehouse ID",
            "Warehouse Name",
            "Address",
            "Available Stock",
            "Status",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading warehouses"
          message="Failed to load warehouse data. Please try again."
        />
      );
    }

    if (warehouses.length === 0) {
      return (
        <EmptyCard
          title="No warehouses found"
          message={
            searchTerm
              ? "No warehouses found matching your search criteria."
              : "No warehouse data available. Create your first warehouse."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Warehouse ID</th>
            <th>Warehouse Name</th>
            <th>Address</th>
            <th>Available Stock</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {warehouses.map((warehouse, index) => (
            <tr key={warehouse.id} className="table-row">
              <td>{(currentPage - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td className="warehouse-id">
                {warehouse.unique_id || warehouse.id}
              </td>
              <td title={warehouse.name || ""}>{warehouse.name}</td>
              <td title={warehouse.address || ""}>{warehouse.address}</td>
              <td>{warehouse.available_devices?.toLocaleString() || 0}</td>
              <td>
                <span
                  className={`inventory-status-badge ${
                    warehouse.is_active ? "active" : "inactive"
                  }`}
                >
                  {warehouse.is_active ? "Active" : "Inactive"}
                </span>
              </td>
              <td>
                <button
                  className="details-btn"
                  onClick={() => handleViewWarehouse(warehouse)}
                >
                  View Warehouse{" "}
                  <ChevronRightIcon
                    style={{ fontSize: "16px", marginLeft: "4px" }}
                  />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inventory-content-container">
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={currentPage}
          setPage={setCurrentPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </div>
  );
};

export default WarehouseList;
